import {
  AfterViewInit,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Input,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, fromEvent, Observable, switchMap, takeUntil, tap } from 'rxjs';

import { markEventAsHandled } from '@blaze-runtime/engine-util';

import { EventChangeEventBase, EventChangeEventType } from '../calendar.models';
import { CalendarEventLayout } from '../layout/calendar-event-layout.model';
import { pixelsToTime } from '../utils/calendar-event.utils';

@UntilDestroy()
@Directive({
  selector: '[blazeEventDragResize]',
  standalone: false,
})
export class EventDragResizeDirective implements AfterViewInit {
  private readonly element = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  isEventDragDisabled = input(false);

  eventChange = output<EventChangeEventBase>();

  @Input('blazeEventDragResize') oneHourHeight!: number;
  @Input() calendarEventLayout!: CalendarEventLayout;

  private readonly isViewInit = signal(false);
  private readonly isEventDragDisabled$: Observable<boolean>;
  private dragHandleTop: HTMLDivElement | undefined;
  private dragHandleBottom: HTMLDivElement | undefined;

  constructor() {
    this.isEventDragDisabled$ = toObservable(this.isEventDragDisabled).pipe(
      filter((isEventDragDisabled) => isEventDragDisabled),
    );

    effect(() => {
      if (!this.isViewInit()) {
        return;
      }

      if (this.isEventDragDisabled()) {
        this.dragHandlesDestroy();
      } else {
        this.dragHandlesInit();
      }
    });
  }

  ngAfterViewInit(): void {
    this.isViewInit.set(true);
  }

  private dragHandlesInit(): void {
    const { partial } = this.calendarEventLayout.event;
    this.dragHandleTop = !partial || partial === 'start' ? this.createDragHandle(true) : undefined;
    this.dragHandleBottom = !partial || partial === 'end' ? this.createDragHandle() : undefined;
    const mouseUp = fromEvent(document, 'mouseup').pipe(
      tap((upEvent) => markEventAsHandled(upEvent)),
    );
    const parentEl = (this.element.nativeElement as HTMLDivElement).parentElement as HTMLDivElement;
    const eventEl = (this.element.nativeElement as HTMLDivElement)
      .firstElementChild as HTMLDivElement;
    if (!parentEl || !eventEl) {
      return;
    } // sanity check

    const roundTo = this.oneHourHeight / 4;
    let eventY = 0;
    let elementY = 0;
    let eventH = 0;
    let elementH = 0;
    let hasMoved = false;

    if (this.dragHandleTop) {
      fromEvent(this.dragHandleTop, 'mousedown')
        .pipe(
          tap((downEvent) => {
            downEvent.stopPropagation();
            eventY = parseInt(parentEl.style.top, 10);
            eventH = parseInt(parentEl.style.height, 10);
            hasMoved = false;
            markEventAsHandled(downEvent);
          }),
          switchMap((downEvent) =>
            fromEvent(document, 'mousemove').pipe(
              tap((moveEvent) => {
                markEventAsHandled(moveEvent);
                moveEvent.preventDefault();
                hasMoved = true;

                const diff = (moveEvent as MouseEvent).pageY - (downEvent as MouseEvent).pageY;
                const diffR = diff - (diff % roundTo);
                elementY = eventY + diffR;
                parentEl.style.top = `${elementY}px`;

                elementH = Math.max(eventH - diffR, roundTo);
                parentEl.style.height = `${elementH}px`;
                eventEl.style.height = `${elementH}px`;
              }),
              takeUntil(mouseUp),
              tap({
                complete: () => {
                  if (hasMoved) {
                    this.emitChanged('start', elementY - eventY);
                  }
                },
              }),
            ),
          ),
          takeUntil(this.isEventDragDisabled$),
          untilDestroyed(this),
        )
        .subscribe();
    }
    if (this.dragHandleBottom) {
      fromEvent(this.dragHandleBottom, 'mousedown')
        .pipe(
          tap((downEvent) => {
            downEvent.stopPropagation();
            eventH = parseInt(parentEl.style.height, 10);
            hasMoved = false;
            markEventAsHandled(downEvent);
          }),
          switchMap((downEvent) =>
            fromEvent(document, 'mousemove').pipe(
              tap((moveEvent) => {
                markEventAsHandled(moveEvent);
                moveEvent.preventDefault();
                hasMoved = true;
                const diff = (moveEvent as MouseEvent).pageY - (downEvent as MouseEvent).pageY;
                const diffR = diff - (diff % roundTo);
                elementH = Math.max(eventH + diffR, roundTo);
                parentEl.style.height = `${elementH}px`;
                eventEl.style.height = `${elementH}px`;
              }),
              takeUntil(mouseUp),
              tap({
                complete: () => {
                  if (hasMoved) {
                    this.emitChanged('end', elementH - eventH);
                  }
                },
              }),
            ),
          ),
          takeUntil(this.isEventDragDisabled$),
          untilDestroyed(this),
        )
        .subscribe();
    }
  }

  private dragHandlesDestroy(): void {
    if (this.dragHandleTop) {
      this.renderer.removeChild(this.element.nativeElement, this.dragHandleTop);
    }
    if (this.dragHandleBottom) {
      this.renderer.removeChild(this.element.nativeElement, this.dragHandleBottom);
    }
  }

  private createDragHandle(placeOnTop = false): HTMLDivElement {
    const div = this.renderer.createElement('div') as HTMLDivElement;
    div.style.position = 'absolute';
    div.style.left = '0';
    div.style.right = '0';
    div.style.height = '5px';
    div.style.cursor = 'row-resize';

    if (placeOnTop) {
      div.style.top = '0';
    } else {
      div.style.bottom = '0';
    }

    this.renderer.appendChild(this.element.nativeElement, div);
    return div;
  }

  private emitChanged(changeType: EventChangeEventType, delta: number): void {
    this.eventChange.emit({ changeType, delta: pixelsToTime(delta, this.oneHourHeight) });
  }
}
